import React, { useEffect, useRef, useState } from "react";
import {
  IoPauseSharp,
  IoPlaySharp,
  IoVolumeHighOutline,
  IoVolumeMuteOutline,
  IoExpandOutline,
  IoContractOutline
} from "react-icons/io5";
import { RiSpeedUpFill } from "react-icons/ri";

interface Props {
  aspectRatio: number;
  src: string;
  index: number;
  setPlayingVideo: (index: number | null) => void;
  playingVideo: number | null;
  detailModal?: boolean;
}

const VideowithAspectRatio: React.FC<Props> = ({
  aspectRatio,
  src,
  index,
  setPlayingVideo,
  playingVideo,
  detailModal = false
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [isPaused, setIsPaused] = useState(false);
  const [muted, setMuted] = useState(true);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [speed, setSpeed] = useState(1);
  const [fullscreen, setFullscreen] = useState(false);

  // Intersection logic for scroll play
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.75) {
            setPlayingVideo(index);
          }
        });
      },
      { threshold: [0.75] }
    );

    if (videoRef.current) observer.observe(videoRef.current);
    return () => observer.disconnect();
  }, [index, setPlayingVideo]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (playingVideo === index) {
      video.play().catch(() => { });
      setIsPaused(false);
    } else {
      video.pause();
    }
  }, [playingVideo, index]);

  // Update timeline
  const updateProgress = () => {
    if (!videoRef.current) return;
    setProgress((videoRef.current.currentTime / videoRef.current.duration) * 100);
    setDuration(videoRef.current.duration);
  };

  const seek = (e: any) => {
    if (!videoRef.current) return;
    const rect = e.target.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    videoRef.current.currentTime =
      (clickX / rect.width) * videoRef.current.duration;
  };

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video.play();
      setIsPaused(false);
    } else {
      video.pause();
      setIsPaused(true);
    }
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !muted;
    setMuted(!muted);
  };

  const toggleSpeed = () => {
    let newSpeed = speed === 2 ? 1 : speed + 0.5;
    setSpeed(newSpeed);
    if (videoRef.current) videoRef.current.playbackRate = newSpeed;
  };

  const toggleFullScreen = () => {

    if (!containerRef.current) return;
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen();
      setFullscreen(true);

    } else {
      document.exitFullscreen();
      setFullscreen(false);
    }
  };

  return (
    <div ref={containerRef} style={{ position: "relative", background: "#000", width: aspectRatio > 630 ? "100%" : "100%" }} >
      <video
        ref={videoRef}
        src={src}
        muted={muted}
        playsInline
        autoPlay
        onTimeUpdate={updateProgress}
        onLoadedMetadata={updateProgress}
        style={{
          width: "100%",
          height: fullscreen
            ? "100vh"
            : aspectRatio > 630
              ? "630px"
              : aspectRatio + "px",
          objectFit: aspectRatio > 630
            ? "contain"
            : "cover",
          position: "relative",
          top: fullscreen
            ? ""
            : detailModal
              ? (aspectRatio < 400 ? "200px" : "50px")
              : "",
        }}

        onError={(e) => console.log("Video error:", e)}
        onEnded={() => {
          setIsPaused(true);
          if (videoRef.current) videoRef.current.currentTime = 0;
        }}
        onClick={(e) => {
          if (fullscreen) {
            e.stopPropagation()
          }
        }}
      />

      {/* Controls */}
      <div
        style={{
          position: "absolute",
          bottom: 0,
          width: "100%",
          background: "rgba(0,0,0,0.6)",
          padding: "10px",
          display: "flex",
          alignItems: "center",
          gap: "12px",
          color: "#fff"
        }}

        onClick={(e) => e.stopPropagation()}
      >
        {/* Play/Pause */}
        <button style={btn} onClick={togglePlay}>
          {isPaused ? <IoPlaySharp size={20} /> : <IoPauseSharp size={20} />}
        </button>

        {/* Seek Bar */}
        <div
          onClick={seek}
          style={{
            flex: 1,
            height: "4px",
            background: "#555",
            borderRadius: "3px",
            cursor: "pointer",
            position: "relative"
          }}
        >
          <div
            style={{
              height: "100%",
              width: `${progress}%`,
              background: "#fff",
              borderRadius: "3px"
            }}
          />
        </div>

        {/* Time */}
        <span style={{ fontSize: "12px", opacity: 0.9, width: 58 }}>
          {videoRef.current
            ? `${formatTime(videoRef.current.currentTime)}`
            : "0:00"} / {formatTime(duration)}
        </span>

        {/* Speed */}
        <button style={btn} onClick={toggleSpeed}>
          <RiSpeedUpFill size={18} /> {speed}x
        </button>

        {/* Volume */}
        <button style={btn} onClick={toggleMute}>
          {muted ? (
            <IoVolumeMuteOutline size={18} />
          ) : (
            <IoVolumeHighOutline size={18} />
          )}
        </button>

        {/* Fullscreen */}
        <button style={btn} onClick={toggleFullScreen}>
          {fullscreen ? (
            <IoContractOutline size={20} />
          ) : (
            <IoExpandOutline size={20} />
          )}
        </button>
      </div>
    </div>
  );
};

// Helpers
const btn: React.CSSProperties = {
  background: "none",
  border: "none",
  color: "#fff",
  cursor: "pointer",
  display: "flex",
  alignItems: "center"
};

const formatTime = (s: number) => {
  if (!s) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60).toString().padStart(2, "0");
  return `${m}:${sec}`;
};

export default React.memo(VideowithAspectRatio);